import { Typography } from '@mui/material';
import { map, reduce, sortBy } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DataGrid, DataGridColumnDefinition } from 'components';
import { DefaultPaymentTypeCode } from 'enums';
import { useQueryColumns } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { MonitoringDataItem, RangeMonitoringDataRow } from 'types';
import { orderMonitoringUtils } from 'utils';

type Props = {
  monitoringData?: MonitoringDataItem[];
  availablePaymentTypes?: DefaultPaymentTypeCode[];
  className?: string;
};

export const PayoutOrdersRangesMonitoring: React.FC<Props> = ({
  monitoringData = [],
  availablePaymentTypes = [],
  className,
}) => {
  const { visibleColumns } = useQueryColumns();
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders_monitoring',
  });

  const columns: DataGridColumnDefinition[] = useMemo(() => {
    const paymentTypeColumns = map(availablePaymentTypes, (paymentType) => ({
      header: orderMonitoringUtils.getPaymentTypeHeader(paymentType),
      valueGetter: (item: any) => {
        const data = item[paymentType];
        return orderMonitoringUtils.getAmountRangeCell(data);
      },
      hidden: visibleColumns?.[paymentType] === false,
      valueClassName: '!tw-font-bold !tw-p-0',
    }));

    return [
      {
        header: t('columns.range'),
        valueKey: 'rangeLabel',
      },
      ...paymentTypeColumns,
    ];
  }, [availablePaymentTypes, visibleColumns, t]);

  const rangeData = useMemo(
    () =>
      reduce(
        monitoringData,
        (acc, item) => {
          const rangeLabel = orderMonitoringUtils.getRangeLabel({
            min: item.minAmount,
            max: item.maxAmount,
          });
          const prev = acc[rangeLabel] || {};
          acc[rangeLabel] = {
            ...prev,
            minAmount: item.minAmount,
            [item.paymentTypeCode]: {
              alertLevel: item.alertLevel,
              label: item.label,
            },
          } as any;
          return acc;
        },
        {} as Record<string, RangeMonitoringDataRow<MonitoringDataItem>>,
      ),
    [monitoringData],
  );

  const rows = useMemo(() => {
    const sortedRanges = sortBy(
      Object.entries(rangeData),
      ([rangeLabel, rangeData]) => rangeData.minAmount,
    );

    return reduce(
      sortedRanges,
      (acc, [rangeLabel, rangeData]) => {
        acc.push({
          rangeLabel,
          ...rangeData,
        });
        return acc;
      },
      [] as any[],
    );
  }, [rangeData]);

  return (
    <div className={className}>
      <Typography className="tw-pb-2" variant="h4">
        {t('sections.amount')}
      </Typography>
      <DataGrid
        tableStyle={{ tableLayout: 'fixed' }}
        columns={columns}
        data={rows}
      />
    </div>
  );
};
